/**
 * A saved query that names a source runs on it for every user (decision DQ), so
 * the product shows which one: a chip on the saved-query row and a line under
 * its result. These are the only provider names outside dev builds (DESIGN §11
 * q3); everything else stays behind `FeatureFlags.sourceLabel`.
 */
import { Chip, Tooltip } from "@mui/material";
import PushPinOutlined from "@mui/icons-material/PushPinOutlined";
import type { ExplorerSource } from "../../../models/Explorer";

// `skill` is left out: it is the draft scope's only source, so pinning to it
// changes nothing a user could see.
const SOURCE_NAMES: Partial<Record<ExplorerSource, string>> = {
  ss: "SecondSpectrum",
  ctg: "Cleaning the Glass",
};

/** The provider name a pinned query shows, or null when there is nothing to show. */
export function pinnedSourceName(
  source: ExplorerSource | string | null | undefined,
): string | null {
  if (!source) return null;
  return SOURCE_NAMES[source as ExplorerSource] ?? null;
}

export function PinnedSourceChip({
  source,
  height = 16,
}: {
  source: ExplorerSource | string | null | undefined;
  height?: number;
}) {
  const name = pinnedSourceName(source);
  if (!name) return null;
  return (
    <Tooltip title={`Pinned: every season in this query reads ${name} data`}>
      <Chip
        data-testid="pinned-source-chip"
        icon={<PushPinOutlined />}
        label={name}
        size="small"
        variant="outlined"
        sx={{
          height,
          fontSize: height <= 16 ? "0.6rem" : "0.7rem",
          borderRadius: "3px",
          borderColor: "#bdbdbd",
          color: "rgba(0, 0, 0, 0.7)",
          "& .MuiChip-label": { px: 0.5 },
          "& .MuiChip-icon": { fontSize: "0.7rem", ml: 0.5, mr: -0.25 },
        }}
      />
    </Tooltip>
  );
}
